<script setup lang="ts">
import { computed } from "vue";
import { UiText, UiIcon, UiTooltip, UiProgressBar, UiInfoBox } from "@shopexperts/shared";
import type { PublicExpertLevel } from "@shopexperts/legacy-api";

export interface WalletSummaryStat {
  key: string;
  label: string;
  value: string;
  tooltip: string;
}

const props = withDefaults(
  defineProps<{
    availableLabel: string;
    balanceCaption: string;
    secondaryStats: WalletSummaryStat[];
    isWalletLoading: boolean;
    currentLevel: PublicExpertLevel | null;
    nextLevel: PublicExpertLevel | null;
    currentTitle: string;
    commissionLabel?: string | null;
    isLevelLoading: boolean;
    hasLevelError?: boolean;
    levelErrorMessage?: string;
    levelTooltip: string;
    percent: number;
    percentLabel: string;
    progressCaption: string;
    remainingToNextLevelLabel?: string | null;
    nextCommissionBenefitLabel?: string | null;
  }>(),
  {
    commissionLabel: null,
    hasLevelError: false,
    levelErrorMessage: "The expert level failed to load. Refresh the page to try again.",
    remainingToNextLevelLabel: null,
    nextCommissionBenefitLabel: null,
  },
);

const hasNextLevel = computed(() => Boolean(props.nextLevel));
</script>

<template>
  <section
    class="font-archivo overflow-hidden rounded-2xl border border-se-gray-300 bg-se-white shadow-sm"
  >
    <div class="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr]">
      <div class="relative flex flex-col gap-6 bg-se-dark p-6 text-se-white sm:p-8">
        <div class="flex items-center gap-2">
          <UiIcon name="wallet" class="h-4 w-4 shrink-0 text-se-white/70" />
          <UiText
            as="span"
            variant="override"
            text="Available balance"
            class="text-[11px] font-semibold uppercase tracking-[0.14em] text-se-white/70"
          />
        </div>

        <div class="flex flex-col gap-1">
          <div v-if="isWalletLoading" class="h-12 w-48 animate-pulse rounded-lg bg-se-white/15" />
          <UiText
            v-else
            as="p"
            variant="override"
            :text="availableLabel"
            class="text-4xl font-bold leading-none tracking-tight sm:text-5xl"
          />
          <UiText
            as="p"
            variant="override"
            :text="balanceCaption"
            class="text-sm text-se-white/60"
          />
        </div>

        <dl class="mt-2 grid grid-cols-1 sm:grid-cols-3">
          <div
            v-for="stat in secondaryStats"
            :key="stat.key"
            class="flex flex-col gap-1 border-b border-se-white/10 bg-se-dark py-4 pr-4 pl-0 last:border-b-0 sm:border-b-0 sm:border-l sm:p-4 sm:first:border-l-0 sm:first:pl-0"
          >
            <dt class="flex items-center gap-1">
              <UiText
                as="span"
                variant="override"
                :text="stat.label"
                class="text-[11px] font-medium uppercase tracking-[0.08em] text-se-white/60"
              />
              <UiTooltip :content="stat.tooltip">
                <UiIcon name="info-circle" class="h-3 w-3 shrink-0 text-se-white/50" />
              </UiTooltip>
            </dt>
            <dd v-if="isWalletLoading" class="h-5 w-16 animate-pulse rounded bg-se-white/15" />
            <UiText
              v-else
              as="span"
              variant="override"
              :text="stat.value"
              class="text-lg font-semibold tabular-nums text-se-white"
            />
          </div>
        </dl>
      </div>

      <div class="flex flex-col gap-5 p-6 sm:p-8">
        <div class="flex items-center justify-between gap-3">
          <UiText
            as="span"
            variant="override"
            text="Expert level"
            class="text-[11px] font-semibold uppercase tracking-[0.14em] text-se-gray-500"
          />
          <span
            v-if="commissionLabel && !isLevelLoading && !hasLevelError"
            class="inline-flex items-center gap-1 rounded-full bg-se-success-light px-2.5 py-1"
          >
            <UiIcon name="dollar-circle" class="h-3.5 w-3.5 shrink-0 text-se-green" />
            <UiText
              as="span"
              variant="override"
              :text="`${commissionLabel} commission`"
              class="text-xs font-semibold text-se-dark"
            />
          </span>
        </div>

        <UiInfoBox v-if="hasLevelError" variant="error" title="Couldn't load level">
          <UiText as="span" variant="p-small">
            <span>{{ levelErrorMessage }}</span>
          </UiText>
        </UiInfoBox>

        <template v-else-if="isLevelLoading">
          <div class="h-7 w-40 animate-pulse rounded bg-se-gray-200" />
          <div class="h-2.5 w-full animate-pulse rounded-full bg-se-gray-200" />
          <div class="h-4 w-48 animate-pulse rounded bg-se-gray-200" />
        </template>

        <template v-else>
          <div class="flex items-center gap-2">
            <UiText
              as="h2"
              variant="override"
              :text="currentTitle"
              class="text-2xl font-bold leading-tight text-se-dark"
            />
            <UiTooltip :content="levelTooltip">
              <UiIcon name="help-circle" class="h-4 w-4 shrink-0 text-se-gray-500" />
            </UiTooltip>
          </div>

          <div class="flex flex-col gap-2">
            <div class="flex items-center justify-between gap-2">
              <UiText
                as="span"
                variant="override"
                :text="hasNextLevel ? `Progress to ${nextLevel?.public_title}` : 'Top level reached'"
                class="text-sm font-medium text-se-gray-700"
              />
              <UiText
                v-if="hasNextLevel"
                as="span"
                variant="override"
                :text="percentLabel"
                class="text-sm font-bold tabular-nums text-se-dark"
              />
            </div>
            <UiProgressBar
              :value="percent"
              tone="green"
              size="md"
              :aria-label="`Progress toward ${nextLevel?.public_title ?? 'next level'}`"
            />
            <UiText
              as="p"
              variant="override"
              :text="progressCaption"
              class="text-xs text-se-gray-500"
            />
          </div>

          <div
            v-if="remainingToNextLevelLabel"
            class="mt-auto flex items-center gap-2 rounded-xl border border-se-gray-200 bg-se-gray-50 px-3 py-2.5"
          >
            <UiIcon name="sparkle" class="h-4 w-4 shrink-0 text-se-green" />
            <div class="flex min-w-0 flex-col gap-0.5">
              <UiText
                as="p"
                variant="override"
                :text="remainingToNextLevelLabel"
                class="text-xs text-se-dark"
              />
              <UiText
                v-if="nextCommissionBenefitLabel"
                as="p"
                variant="override"
                :text="nextCommissionBenefitLabel"
                class="text-xs text-se-gray-500"
              />
            </div>
          </div>
        </template>
      </div>
    </div>
  </section>
</template>
